<script>
  export let filters = {
    categories: {
      web: false,
      app: false,
      server: false,
      frontend: false,
      fullstack: false,
      database: false
    },
    skills: {},
    experience: {
      beginner: false,
      intermediate: false,
      advanced: false,
      any: false
    },
    workTypes: {
      onsite: false,
      other: false
    },
    status: {
      excludeClosed: true,
      excludeApplied: false
    },
    region: {
      selectedCity: ''
    }
  };
  export let skillsData = [];
  export let expandedSkills = {};
  export let onResetFilters = () => {};
  export let onApplyFilters = () => {};
  export let onToggleSkill = () => {};
  
  // 검색 관련
  export let searchTerm = '';
  export let onSearch = () => {};
  export let onKeyup = () => {};
  
  function handleKeyup(event) {
    onKeyup(event);
  }
  
  import { onMount } from 'svelte';
  import { API_BASE_URL } from '../../config.js';
  
  // 지역 데이터
  let cities = [];
  
  // 지역 데이터 캐시
  let regionCache = {
    cities: null
  };
  
  // 지역 데이터 로드 (캐시 사용)
  async function loadCities() {
    try {
      if (regionCache.cities) {
        console.log('시 데이터 캐시 사용');
        cities = regionCache.cities;
        return;
      }
      
      console.log('시 데이터 API 호출');
      const response = await fetch(`${API_BASE_URL}/api/codes/regions/cities`);
      const result = await response.json();
      if (result.success) {
        // "전국" 항목 필터링
        const filteredCities = result.data.filter(city => city.code_name !== '전국');
        cities = filteredCities;
        regionCache.cities = filteredCities; // 필터링된 데이터 캐시 저장
      }
    } catch (error) {
      console.error('지역(시) 목록 로드 오류:', error);
    }
  }
  
  
  onMount(() => {
    loadCities();
  });

  // 필터 접기/펼치기 상태
  let isFilterCollapsed = true;

  function toggleFilter() {
    isFilterCollapsed = !isFilterCollapsed;
  }

  // 깊이별 스킬 가져오기
  function getSkillsByDepth(depth) {
    return skillsData.filter(skill => skill.depth === depth);
  }

  // 자식 스킬 가져오기 (App.svelte와 동일한 로직)
  function getChildSkills(parentCode) {
    return skillsData.filter(skill => 
      skill.code.startsWith(parentCode) && 
      skill.code !== parentCode && 
      skill.code.length === parentCode.length + 2
    );
  }

  // 상위 스킬 선택 시 하위 스킬들 자동 선택/해제
  function handleParentSkillChange(parentCode, isChecked) {
    // 상위 스킬 상태 업데이트
    filters.skills[parentCode] = isChecked;
    
    // 하위 스킬들도 동일한 상태로 업데이트
    const childSkills = getChildSkills(parentCode);
    childSkills.forEach(childSkill => {
      filters.skills[childSkill.code] = isChecked;
    });
    
    // 필터 상태 반영
    filters = filters;
    
    // 자동으로 필터 적용
    onApplyFilters();
  }

  // 하위 스킬 선택 시 상위 스킬 상태 확인 및 업데이트
  function handleChildSkillChange(childCode, isChecked) {
    // 하위 스킬 상태 업데이트
    filters.skills[childCode] = isChecked;
    
    // 부모 스킬 찾기 (코드 길이를 기반으로)
    const parentCode = childCode.substring(0, childCode.length - 2);
    const parentSkill = skillsData.find(skill => skill.code === parentCode);
    
    if (parentSkill) {
      const childSkills = getChildSkills(parentCode);
      
      // 모든 하위 스킬이 체크되어 있는지 확인
      const allChildrenChecked = childSkills.every(child => filters.skills[child.code]);
      
      // 상위 스킬 상태 업데이트
      filters.skills[parentCode] = allChildrenChecked;
    }
    
    // 필터 상태 반영
    filters = filters;
    
    // 자동으로 필터 적용
    onApplyFilters();
  }
</script>

<div class="filter-container">
  <div class="filter-header">
    <div class="filter-title-section">
      <h3>프로젝트 필터</h3>
      <button 
        class="filter-toggle" 
        on:click={toggleFilter}
        aria-label={isFilterCollapsed ? '필터 펼치기' : '필터 접기'}
      >
        <span class="toggle-icon">{isFilterCollapsed ? '▼' : '▲'}</span>
      </button>
    </div>
    
    <!-- 검색 입력 -->
    <div class="search-input-container">
      <input 
        type="text" 
        class="search-input"
        placeholder="프로젝트, 기술 스택 검색" 
        bind:value={searchTerm} 
        on:keyup={handleKeyup}
      >
    </div>
    
    <div class="filter-actions">
      <button class="filter-reset" on:click={onResetFilters}>초기화</button>
      <button class="filter-apply" on:click={onApplyFilters}>검색</button>
    </div>
  </div>
  
  {#if !isFilterCollapsed}
  <div class="filter-groups-horizontal" class:collapsed={isFilterCollapsed}>
    <!-- 기술 스택 필터 -->
    <div class="filter-group">
      <h4>기술 스택</h4>
      <div class="filter-content">
        <div class="skill-tree-horizontal">
          {#each getSkillsByDepth(2) as skill}
            <div class="skill-item">
              <div 
                class="skill-parent" 
                on:click={() => onToggleSkill(skill.code)}
                on:keydown={(e) => e.key === 'Enter' && onToggleSkill(skill.code)}
                tabindex="0"
                role="button"
              >
                <span class="toggle-icon">{expandedSkills[skill.code] ? '▼' : '►'}</span>
                <label>
                  <input 
                    type="checkbox" 
                    checked={filters.skills[skill.code] || false}
                    on:change={(e) => {
                      e.stopPropagation();
                      handleParentSkillChange(skill.code, e.target.checked);
                    }}
                  >&nbsp;{skill.name}
                </label>
              </div>
              
              {#if expandedSkills[skill.code]}
                <div class="sub-skills">
                  {#each getChildSkills(skill.code) as childSkill}
                    <label class="child-skill">
                      <input 
                        type="checkbox" 
                        checked={filters.skills[childSkill.code] || false}
                        on:change={(e) => handleChildSkillChange(childSkill.code, e.target.checked)}
                      > {childSkill.name}
                    </label>
                  {/each}
                </div>
              {/if}
            </div>
          {/each}
        </div>
      </div>
    </div>
    
    <!-- 개발 분야 필터 -->
    <div class="filter-group">
      <h4>개발 분야</h4>
      <div class="filter-content">
        <div class="checkbox-grid">
          <label><input type="checkbox" bind:checked={filters.categories.web} on:change={onApplyFilters}> 웹 개발</label>
          <label><input type="checkbox" bind:checked={filters.categories.app} on:change={onApplyFilters}> 앱 개발</label>
          <label><input type="checkbox" bind:checked={filters.categories.server} on:change={onApplyFilters}> 서버/백엔드</label>
          <label><input type="checkbox" bind:checked={filters.categories.frontend} on:change={onApplyFilters}> 프론트엔드</label>
          <label><input type="checkbox" bind:checked={filters.categories.fullstack} on:change={onApplyFilters}> 풀스택</label>
          <label><input type="checkbox" bind:checked={filters.categories.database} on:change={onApplyFilters}> 데이터베이스</label>
        </div>
      </div>
    </div>
    
    <!-- 구분 필터 -->
    <div class="filter-group">
      <h4>구분</h4>
      <div class="filter-content">
        <div class="checkbox-grid">
          <label><input type="checkbox" bind:checked={filters.experience.beginner} on:change={onApplyFilters}> 초급</label>
          <label><input type="checkbox" bind:checked={filters.experience.intermediate} on:change={onApplyFilters}> 중급</label>
          <label><input type="checkbox" bind:checked={filters.experience.advanced} on:change={onApplyFilters}> 고급</label>
          <label><input type="checkbox" bind:checked={filters.experience.any} on:change={onApplyFilters}> 무관</label>
        </div>
      </div>
    </div>
    
    <!-- 지역 필터 -->
    <div class="filter-group">
      <h4>지역</h4>
      <div class="filter-content">
        <div class="region-selects">
          <div class="select-wrapper">
            <select 
              id="city-select" 
              bind:value={filters.region.selectedCity}
              on:change={onApplyFilters}
              class="region-select"
            >
              <option value="">전체</option>
              {#each cities as city}
                <option value={city.code_value}>{city.code_name}</option>
              {/each}
            </select>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 상태 및 근무형태 그룹 -->
    <div class="filter-group-stack">
      <!-- 근무 형태 필터 -->
      <div class="filter-group-stacked-item">
        <h4>근무 형태</h4>
        <div class="filter-content">
          <div class="checkbox-grid">
            <label><input type="checkbox" bind:checked={filters.workTypes.onsite} on:change={onApplyFilters}> 상주</label>
            <div class="tooltip-wrapper">
              <label><input type="checkbox" bind:checked={filters.workTypes.other} on:change={onApplyFilters}> 기타</label>
              <div class="tooltip">재택 등 기타</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 프로젝트 상태 필터 -->
      <div class="filter-group-stacked-item">
        <h4>프로젝트 상태</h4>
        <div class="filter-content">
          <div class="checkbox-grid">
            <label><input type="checkbox" bind:checked={filters.status.excludeClosed} on:change={onApplyFilters}> 마감 제외</label>
            <label><input type="checkbox" bind:checked={filters.status.excludeApplied} on:change={onApplyFilters}> 지원한 공고 제외</label>
          </div>
        </div>
      </div>
    </div>
  </div>
  {/if}
</div>

<style>
  .filter-container {
    width: 100%;
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    margin-bottom: 24px;
    padding: 20px;
  }

  .filter-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 12px;
    border-bottom: 2px solid #e9ecef;
  }

  .filter-title-section {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .filter-header h3 {
    font-size: 1.3rem;
    font-weight: 600;
    color: #2c3e50;
    margin: 0;
  }

  .filter-toggle {
    background: none;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    color: #6c757d;
  }

  .filter-toggle:hover {
    background-color: #f8f9fa;
    border-color: #adb5bd;
    color: #495057;
  }

  .filter-toggle .toggle-icon {
    font-size: 0.9rem;
    font-weight: bold;
    width: auto;
    margin: 0;
  }

  .search-input-container {
    flex: 1;
    position: relative;
    margin: 0 16px;
  }

  .search-icon {
    position: absolute;
    top: 50%;
    left: 18px;
    transform: translateY(-50%);
    color: #6c757d;
    pointer-events: none;
  }

  .search-input {
    width: 100%;
    padding: 16px 20px 16px 50px;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    font-size: 1.1rem;
    background-color: white;
    box-sizing: border-box;
    min-height: 50px;
  }

  .search-input:focus {
    outline: none;
    border-color: #3273dc;
    box-shadow: 0 0 0 2px rgba(50, 115, 220, 0.2);
  }

  .search-input::placeholder {
    color: #6c757d;
  }

  .filter-actions {
    display: flex;
    gap: 10px;
  }

  .filter-groups-horizontal {
    display: flex;
    gap: 24px;
    flex-wrap: wrap;
    align-items: flex-start;
    animation: slideDown 0.3s ease-out;
    overflow: hidden;
  }

  .filter-groups-horizontal.collapsed {
    animation: slideUp 0.3s ease-out forwards;
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      max-height: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      max-height: 1000px;
      transform: translateY(0);
    }
  }

  @keyframes slideUp {
    from {
      opacity: 1;
      max-height: 1000px;
      transform: translateY(0);
    }
    to {
      opacity: 0;
      max-height: 0;
      transform: translateY(-10px);
    }
  }

  .filter-group {
    flex: 1;
    min-width: 180px;
    background-color: #f8f9fa;
    border-radius: 6px;
    padding: 16px;
  }

  .filter-group h4, .filter-group-stacked-item h4 {
    font-size: 0.95rem;
    font-weight: 600;
    margin-bottom: 12px;
    color: #495057;
    text-align: center;
    padding-bottom: 8px;
    border-bottom: 1px solid #dee2e6;
  }

  .filter-content {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .checkbox-grid {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .checkbox-grid label {
    display: flex;
    align-items: center;
    font-size: 0.9rem;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
    transition: background-color 0.2s;
  }

  .checkbox-grid label:hover {
    background-color: white;
  }

  .checkbox-grid input[type="checkbox"] {
    margin-right: 8px;
    width: 16px;
    height: 16px;
  }

  /* 스택 그룹 스타일 */
  .filter-group-stack {
    flex: 1;
    min-width: 180px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .filter-group-stacked-item {
    background-color: #f8f9fa;
    border-radius: 6px;
    padding: 16px;
    width: 100%;
  }

  /* 기술 스택 횡방향 레이아웃 */
  .skill-tree-horizontal {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .skill-item {
    background-color: white;
    border-radius: 4px;
    padding: 8px;
  }

  .skill-parent {
    display: flex;
    align-items: center;
    cursor: pointer;
    padding: 2px 4px;
    border-radius: 3px;
    transition: background-color 0.2s;
  }

  .skill-parent:hover {
    background-color: #e9ecef;
  }

  .skill-parent label {
    display: flex;
    align-items: center;
    font-size: 0.9rem;
    cursor: pointer;
    margin: 0;
  }

  .toggle-icon {
    display: inline-block;
    width: 16px;
    font-size: 0.7rem;
    color: #6c757d;
    margin-right: 4px;
  }

  .sub-skills {
    margin-top: 8px;
    margin-left: 20px;
    padding: 8px;
    background-color: #f8f9fa;
    border-radius: 3px;
    border-left: 3px solid #dee2e6;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .child-skill {
    display: flex;
    align-items: center;
    font-size: 0.85rem;
    cursor: pointer;
    padding: 2px 6px;
    background-color: white;
    border-radius: 3px;
    border: 1px solid #e9ecef;
    transition: all 0.2s;
  }

  .child-skill:hover {
    background-color: #e3f2fd;
    border-color: #90caf9;
  }

  .child-skill input[type="checkbox"] {
    margin-right: 6px;
    width: 14px;
    height: 14px;
  }

  .filter-reset, .filter-apply {
    padding: 10px 16px;
    border: none;
    border-radius: 6px;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
  }

  .filter-reset {
    background-color: #6c757d;
    color: white;
  }

  .filter-reset:hover {
    background-color: #5a6268;
  }

  .filter-apply {
    background-color: #007bff;
    color: white;
  }

  .filter-apply:hover {
    background-color: #0056b3;
  }

  /* 지역 선택 스타일 */
  .region-selects {
    display: flex;
    justify-content: center;
  }

  .select-wrapper {
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: 100%;
    max-width: 200px;
  }

  .select-wrapper label {
    font-size: 0.9rem;
    font-weight: 500;
    color: #495057;
    margin: 0;
  }

  .region-select {
    padding: 8px 12px;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    font-size: 0.9rem;
    background-color: white;
    cursor: pointer;
    transition: border-color 0.2s;
  }

  .region-select:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
  }

  .region-select:disabled {
    background-color: #f8f9fa;
    cursor: not-allowed;
    opacity: 0.6;
  }

  /* 툴팁 */
  .tooltip-wrapper {
    position: relative;
    display: inline-block;
  }

  .tooltip {
    visibility: hidden;
    width: 140px;
    background-color: #555;
    color: white;
    text-align: center;
    border-radius: 4px;
    padding: 6px;
    position: absolute;
    z-index: 1000;
    bottom: 125%;
    left: 50%;
    transform: translateX(-50%);
    opacity: 0;
    transition: opacity 0.3s;
    font-size: 0.75rem;
    pointer-events: none;
  }

  .tooltip::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: #555 transparent transparent transparent;
  }

  .tooltip-wrapper:hover .tooltip {
    visibility: visible;
    opacity: 1;
  }

  /* 반응형 디자인 */
  @media (max-width: 1200px) {
    .filter-groups-horizontal {
      flex-direction: column;
    }
    
    .filter-group, .filter-group-stack {
      flex: none;
      min-width: auto;
    }
  }
  
  @media (max-width: 992px) and (min-width: 769px) {
    .filter-groups-horizontal {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 16px;
    }
    
    /* 기술 스택은 전체 폭 사용 */
    .filter-group:first-child {
      grid-column: 1 / -1;
    }
  }

  @media (max-width: 768px) {
    .filter-container {
      padding: 16px;
      margin-bottom: 20px;
    }
    
    .filter-header {
      flex-direction: row;
      gap: 12px;
      align-items: center;
      justify-content: space-between;
    }
    
    .filter-title-section {
      justify-content: flex-start;
      align-items: center;
    }

    .search-input-container {
      margin: 0;
    }
    
    .filter-actions {
      justify-content: flex-end;
      flex-shrink: 0;
    }
    
    .filter-toggle {
      width: 44px;
      height: 44px;
    }

    .search-icon {
      left: 14px;
    }
    
    .search-input {
      height: 44px;
    }

    .filter-actions .filter-reset,
    .filter-actions .filter-apply {
      height: 44px;
      padding: 0 16px;
      font-size: 0.85rem;
    }
    
    /* 모바일에서 필터 그룹을 2열 그리드로 배치 */
    .filter-groups-horizontal {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
    }
    
    .filter-group, .filter-group-stack {
      padding: 12px;
      min-height: auto;
    }
    
    .filter-group h4, .filter-group-stacked-item h4 {
      font-size: 0.9rem;
      margin-bottom: 8px;
    }
    
    /* 기술 스택은 전체 폭 사용 */
    .filter-group:first-child {
      grid-column: 1 / -1;
    }
    
    /* 체크박스 그리드를 더 컴팩트하게 */
    .checkbox-grid {
      gap: 6px;
    }
    
    .checkbox-grid label {
      font-size: 0.85rem;
      padding: 2px 6px;
    }
    
    /* 기술 스택 트리를 더 컴팩트하게 */
    .skill-tree-horizontal {
      gap: 4px;
    }
    
    .skill-item {
      padding: 6px;
    }
    
    .sub-skills {
      margin-top: 6px;
      margin-left: 12px;
      padding: 6px;
      gap: 4px;
    }
    
    .child-skill {
      font-size: 0.8rem;
      padding: 1px 4px;
    }
  }
  
  /* 더 작은 화면에서는 1열로 */
  @media (max-width: 480px) {
    .filter-groups-horizontal {
      grid-template-columns: 1fr;
      gap: 10px;
    }
    
    .filter-group:first-child, .filter-group-stack {
      grid-column: auto;
    }
    
    .filter-header {
      flex-direction: column;
      gap: 10px;
      align-items: stretch;
    }
    
    .filter-actions {
      justify-content: center;
    }
  }
</style>